/**
 * "Is this job's packet complete enough to send?" (#1569)
 *
 * One shared read behind the two screens that ask: the job page's Resend
 * dialog, and Close Out's action bar before the first send. They ask the same
 * question and must never answer it differently — the #832 lesson, where two
 * surfaces described the same Cintas send in two different numbers.
 *
 * The distinction that matters, and the one the endpoint's older fields get
 * wrong for this purpose: `waiting_on_reports` / `waiting_on_work_order`
 * answer "what is the PARKED row waiting on", so they are both false on a job
 * nobody has approved yet. The job page needs "what WOULD hold this if you
 * pressed send now" — that is `reports_ready` / `work_order_ready`.
 *
 * Why this exists at all: on 2026-09-17 a resend appeared to do nothing. It
 * had parked, because two of six devices had no report filed. The endpoint
 * already knew that before the press — its own comment says it reports
 * readiness "for jobs not (yet) held so the office can see what would hold
 * before approving" — but only Close Out read it, and only after the fact.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/** A tested device with no report attached yet. */
export interface UnreadyDevice {
    assetId: string;
    name: string;
    serialNumber: string | null;
    assemblyType: string | null;
    locationDescription: string | null;
}

export interface PacketReadiness {
    /** A packet is parked on this job right now. */
    held: boolean;
    parked_at: string | null;
    reports: { filed: number; total: number; missing: UnreadyDevice[] };
    /** Every tested device has a report. */
    reports_ready: boolean;
    /** Not a work-order job, or its completed WO is attached. */
    work_order_ready: boolean;
    /** What the HELD row waits on — false on a job never approved. */
    waiting_on_reports: boolean;
    waiting_on_work_order: boolean;
    wo_applicable: boolean;
    wo_present: boolean;
    /** The purveyor files through an online portal, so a missing report means
     *  the CITY hasn't returned it — not something the office can fix by
     *  attaching a scan. Changes the wording and where we send them. */
    portal?: boolean;
    /** A held packet whose every address is ours: it won't go by itself (#1759). */
    no_customer_email?: boolean;
    /** The customer's taught delivery route (#1738), which presets
     *  "Already sent another way". Older servers omit it. */
    delivery_route?: { route: "portal" | "mail" | "contact"; label: string | null; scope: string | null } | null;
}

export function usePacketReadiness(jobId: string | undefined, enabled = true) {
    return useQuery<PacketReadiness>({
        queryKey: ["packet-readiness", jobId],
        queryFn: () => api.get<PacketReadiness>(`/api/office-review/jobs/${jobId}/packet-status`),
        enabled: !!jobId && enabled,
        staleTime: 15_000,
    });
}

/** Why a packet can't go out yet, or null when it can. */
export type PacketBlock =
    | { kind: "reports"; missing: UnreadyDevice[]; filed: number; total: number; portal: boolean }
    | { kind: "work_order" };

/**
 * The single decision both screens render from.
 *
 * Reports are reported ahead of the work order when both are outstanding: the
 * reports are the office's own to chase and usually the longer wait, and one
 * clear instruction beats two competing ones.
 */
export function packetBlock(r: PacketReadiness | undefined): PacketBlock | null {
    if (!r) return null;
    if (!r.reports_ready) {
        return {
            kind: "reports",
            missing: r.reports.missing ?? [],
            filed: r.reports.filed,
            total: r.reports.total,
            portal: r.portal === true,
        };
    }
    if (!r.work_order_ready) return { kind: "work_order" };
    return null;
}

/** "Irrigation — RP" / "Irrigation" when the assembly type isn't recorded. */
export function deviceLabel(d: UnreadyDevice): string {
    return d.assemblyType ? `${d.name} — ${d.assemblyType}` : d.name;
}
